import { Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import React, { useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const SAMPLE_HEADERS = ["Name", "Phone", "Address", "Opening Balance", "Opening Balance Date"];
const SAMPLE_ROWS = [
  ["ABC Traders", "9876543210", "123 Main Street, City", "0", "2024-01-01"],
  ["XYZ Supplies", "9123456789", "456 Park Avenue, Town", "1000", "2024-01-15"],
];

const downloadSampleFormat = () => {
  const ws = XLSX.utils.aoa_to_sheet([SAMPLE_HEADERS, ...SAMPLE_ROWS]);
  const colWidths = [{ wch: 20 }, { wch: 14 }, { wch: 35 }, { wch: 16 }, { wch: 20 }];
  ws["!cols"] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Suppliers");
  XLSX.writeFile(wb, "supplier_import_format.xlsx");
};

// Normalize date: Excel may return serial number; convert to YYYY-MM-DD
const normalizeDate = (val) => {
  if (val == null || val === "") return undefined;
  if (typeof val === "number" && val > 1000) {
    const d = new Date((val - 25569) * 86400 * 1000);
    return isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
  }
  const s = String(val).trim();
  return s || undefined;
};

const normalizeKey = (key) => {
  if (!key || typeof key !== "string") return "";
  const k = key.toString().trim().toLowerCase().replace(/\s+/g, "_");
  if (k === "name" || k === "supplier_name") return "name";
  if (k === "phone" || k === "mobile" || k === "contact") return "phone";
  if (k === "address" || k === "addr") return "address";
  if (k === "opening_balance" || k === "openingbalance") return "opening_balance";
  if (k === "opening_balance_date" || k === "openingbalancedate" || k === "date") return "opening_balance_date";
  return key.trim();
};

const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];
  const headerLine = lines[0];
  const headers = headerLine.split(",").map((h) => normalizeKey(h.trim()));
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(",").map((v) => v.trim());
    const obj = {};
    headers.forEach((h, idx) => {
      if (h) obj[h] = values[idx] != null ? values[idx] : "";
    });
    if (obj.name) rows.push(obj);
  }
  return rows;
};

const parseExcel = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheet];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
        if (!json.length) {
          resolve([]);
          return;
        }
        const rawHeaders = json[0].map((h) => (h != null ? String(h) : ""));
        const headers = rawHeaders.map((h) => normalizeKey(h));
        const rows = [];
        for (let i = 1; i < json.length; i++) {
          const row = json[i];
          const obj = {};
          headers.forEach((h, idx) => {
            if (h) obj[h] = row[idx] != null ? String(row[idx]).trim() : "";
          });
          if (obj.name) rows.push(obj);
        }
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
};

const UploadSupplierExcel = ({ onSuccess }) => {
  const [loader, setLoader] = useState(false);
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      message.warning("Please select a CSV or Excel file.");
      return;
    }
    const fileName = (file.name || "").toLowerCase();
    const isCsv = fileName.endsWith(".csv");
    const isExcel = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");
    if (!isCsv && !isExcel) {
      message.warning("Please upload a .csv, .xlsx, or .xls file.");
      return;
    }

    setLoader(true);
    try {
      let rows = [];
      if (isCsv) {
        const text = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsText(file);
        });
        rows = parseCSV(text);
      } else {
        rows = await parseExcel(file);
      }

      rows = rows
        .map((row) => {
          const name = String(row.name || "").trim();
          if (!name) return null;
          const item = {
            name,
            phone: String(row.phone || "").trim() || undefined,
            address: String(row.address || "").trim() || undefined,
          };
          if (row.opening_balance != null && row.opening_balance !== "") {
            const ob = parseFloat(row.opening_balance);
            if (!isNaN(ob)) item.opening_balance = ob;
          }
          const obDate = normalizeDate(row.opening_balance_date);
          if (obDate) item.opening_balance_date = obDate;
          return item;
        })
        .filter(Boolean);

      if (rows.length === 0) {
        message.warning("No valid rows (name is required).");
        setLoader(false);
        return;
      }

      const url = "supplier?query=createmany&upsert=true";
      const { data } = await axios.post(url, rows);
      setLoader(false);
      setFile(null);
      e.target.reset();

      if (data.created !== undefined && data.updated !== undefined) {
        toast.success(`Import done: ${data.created} created, ${data.updated} updated.`);
      } else {
        const count = data.count != null ? data.count : rows.length;
        toast.success(`Imported ${count} supplier(s) successfully.`);
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      setLoader(false);
      const msg = err.response?.data?.message || err.message || "Import failed.";
      toast.error(msg);
    }
  };

  return (
    <div className="text-center mt-2">
      <Button
        type="default"
        icon={<DownloadOutlined />}
        onClick={downloadSampleFormat}
        className="mb-2"
      >
        Download sample format (.xlsx)
      </Button>
      <form onSubmit={handleSubmit}>
        <input
          className="form-control"
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
        />
        <br />
        <Button
          className="mt-2"
          type="primary"
          htmlType="submit"
          shape="round"
          loading={loader}
        >
          Import from Excel
        </Button>
      </form>
    </div>
  );
};

export default UploadSupplierExcel;
